import { SpokeLoading } from '@doltech/core/lib/auth/SpokeLoading';
import { IconContentLink } from '@doltech/icons/IconContentLink';
import { IconOtherClose } from '@doltech/icons/IconOtherClose';
import { LinkSettingEnum, LinkActionTypeEnum } from '@doltech/utils/lib/constants';
import { checkUrlValid } from '@doltech/utils/lib/url';
import { Checkbox, Form, Radio, Space } from 'antd';
import cl from 'classnames';
import * as React from 'react';
import styled from 'styled-components';

import { getPreventDefaultHandler } from '../../../../../slatePlugins/common/utils/getPreventDefaultHandler';
import { Button } from '../../../../ButtonV2/ButtonV2';
import { colorsV2 } from '../../../../colors-v2';
import { Box } from '../../../../Common/BoxV2';
import { Typography } from '../../../../DesignSystemV2/Typography';
import { Input } from '../../../../Form/InputV2/InputV2';
import { shadows } from '../../../../shadows';
import { noop } from 'lodash';

const BodySection = styled.section`
  .ant-form-item {
    margin-bottom: 0;
  }
`;

const LinkOnlyBodySection = styled.section`
  .ant-form-item {
    margin-bottom: 0;
  }
  &&& {
    .dol-inputV2 {
      border-color: transparent;
      background: ${colorsV2.gray10};
      box-shadow: unset;
      &:hover,
      &:focus,
      &:active {
        border-color: transparent;
        background: ${colorsV2.gray10};
        box-shadow: unset;
      }
    }
  }
`;

const HeaderSection = styled.section`
  display: flex;
  align-items: center;

  .title {
    flex: 1;
  }
`;

const OptionsWrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
  margin-top: 12px;
`;

export interface HyperLinkInputProps {
  onApplyLink?: (url: string, linkSettings?: string[], linkAction?: string) => void;
  onMouseDown?: (e?) => void;
  onClose?: (e?) => void;
  form?: any;
  baseUrl?: string;
  title?: string;
  mode?: 'popup' | 'inline';
  apiFn?: (url: string) => Promise<any>;
  withLinkSetting?: boolean;
  onChange?: (e: any) => void;
  withTitle?: boolean;
}

const Wrapper = styled(Box)`
  display: grid;
  gap: 12px;
  grid-template-rows: max-content max-content;
  .dol-inputV2 {
    height: 56px;
  }
  .dol-inputV2.ant-input,
  .dol-inputV2.ant-input-affix-wrapper {
    max-height: 56px;
    width: 496px;
    padding: 8px;
    border-radius: 16px;
  }

  &.popup {
    border-radius: 12px;
    background-color: ${colorsV2.white100};
  }

  &.inline {
    background: ${colorsV2.gray10};
    border: 1px dashed ${colorsV2.gray40};
    border-radius: 16px;
    font-family: Inter, sans-serif;
    .ant-input-prefix {
      margin-right: 16px;
      font-size: 20px;
      padding-left: 8px;
    }
    .dol-inputV2.ant-input,
    .dol-inputV2.ant-input-affix-wrapper {
      width: 100%;
      background-color: ${colorsV2.white100};
    }
    input.ant-input {
      height: 100%;
    }
  }
`;

export const HyperLinkInput = React.forwardRef(
  (props: HyperLinkInputProps, ref: React.Ref<HTMLDivElement>) => {
    const {
      title = 'Link',
      onApplyLink,
      onChange = noop,
      onClose,
      form,
      mode = 'popup',
      apiFn,
      withLinkSetting = false,
      withTitle = true,
      ...rest
    } = props;
    const [defaultForm] = Form.useForm();
    const [applyLoading, setApplyLoading] = React.useState(false);

    const renderInput = React.useCallback(() => {
      return (
        <Form
          form={form || defaultForm}
          validateTrigger="onSubmit"
          onFinish={(values) => {
            onApplyLink(
              values.url,
              values.linkSettings,
              values.linkAction || LinkActionTypeEnum.AUTO
            );
          }}
        >
          <Form.Item
            name="url"
            rules={[
              {
                validator: async (_, value) => {
                  setApplyLoading(true);

                  if (apiFn) {
                    const res = await apiFn(value);
                    if (res) {
                      setApplyLoading(false);
                      return res;
                    }
                  } else {
                    try {
                      if (checkUrlValid(value)) {
                        return;
                      }
                    } catch (error) {
                      // eslint-disable-next-line no-console
                      console.error('error', error);
                    } finally {
                      setApplyLoading(false);
                    }
                  }

                  setApplyLoading(false);
                  return Promise.reject(new Error('URL không tồn tại, vui lòng nhập lại.'));
                },
                message: 'URL không tồn tại, vui lòng nhập lại.',
              },
            ]}
          >
            <Input
              style={{ height: 56, maxHeight: 56 }}
              placeholder="Nhập link"
              prefix={<IconContentLink />}
              onChange={onChange}
              suffix={
                <Button.Primary
                  variant="outline"
                  onMouseDown={getPreventDefaultHandler((form || defaultForm).submit)}
                >
                  {applyLoading ? (
                    <SpokeLoading color={colorsV2.black100} width={24} height={24} />
                  ) : (
                    'Apply'
                  )}
                </Button.Primary>
              }
              onFocus={(event) => event.preventDefault()}
              onFocusCapture={(event) => event.preventDefault()}
              onMouseEnter={(event) => event.preventDefault()}
            />
          </Form.Item>

          {withLinkSetting && (
            <OptionsWrapper>
              <Box>
                <Typography.Title level={4} variant="semi-bold/16" mb="12px">
                  Link Setting
                </Typography.Title>

                <Form.Item name="linkSettings">
                  <Checkbox.Group>
                    <Box mb="12px">
                      <Checkbox className="dol-checkbox" value={LinkSettingEnum.OPEN_NEW_TAB}>
                        <Typography.Text variant="medium/14">Open New tab</Typography.Text>
                      </Checkbox>
                    </Box>
                    <Box>
                      <Checkbox className="dol-checkbox" value={LinkSettingEnum.NO_FOLLOW}>
                        <Typography.Text variant="medium/14">No Follow</Typography.Text>
                      </Checkbox>
                    </Box>
                  </Checkbox.Group>
                </Form.Item>
              </Box>
              <Box>
                <Typography.Title level={4} variant="semi-bold/16" mb="12px">
                  Link Action
                </Typography.Title>

                <Form.Item name="linkAction">
                  <Radio.Group defaultValue={LinkActionTypeEnum.AUTO}>
                    <Space direction="vertical">
                      <Radio value={LinkActionTypeEnum.AUTO}>Auto</Radio>
                      <Radio value={LinkActionTypeEnum.COPY_TO_CLIPBOARD}>Copy to clipboard</Radio>
                      <Radio value={LinkActionTypeEnum.DIRECT}>Link</Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
              </Box>
            </OptionsWrapper>
          )}
        </Form>
      );
    }, [apiFn, applyLoading, defaultForm, form, onApplyLink, onChange, withLinkSetting]);

    if (!withTitle) {
      return <LinkOnlyBodySection>{renderInput()}</LinkOnlyBodySection>;
    }

    return (
      <Wrapper
        ref={ref}
        p={16}
        boxShadow={shadows.zIndex4}
        className={cl('hyperlink-input', ['popup', 'inline'].includes(mode) ? mode : 'popup')}
        {...rest}
      >
        <HeaderSection>
          <Typography.Title className="title font-inter" level={4} variant="semi-bold/16">
            {title}
          </Typography.Title>
          {mode === 'popup' && (
            <Button.Icon type="secondary" size="medium" onClick={getPreventDefaultHandler(onClose)}>
              <IconOtherClose />
            </Button.Icon>
          )}
        </HeaderSection>
        <BodySection>{renderInput()}</BodySection>
      </Wrapper>
    );
  }
);
